import React, { useMemo } from 'react'
import { generateSocketPositions, BRICK_CONSTANTS } from '@utils/brickGeometry'
import type { BrickType, BrickColor } from '@models/brick'

interface BrickSocketsProps {
  type: BrickType
  rotation: number
  color: BrickColor
  visible?: boolean // Whether to show sockets (useful for debugging)
}

/**
 * BrickSockets Component
 *
 * Renders the socket holes on the bottom of a brick.
 * Sockets receive studs from bricks below for vertical stacking.
 *
 * Features:
 * - Hollow cylinder geometry (tube shape)
 * - Positioned relative to parent brick (use as child of mesh)
 * - Slightly darker than brick body to show depth
 * - Optional visibility toggle (can be hidden on placed bricks)
 * - Used for connection detection in SnappingEngine
 *
 * Visual Design:
 * - Inner diameter: 0.42 units (slightly larger than stud for fit)
 * - Wall thickness: 0.05 units
 * - Depth: extends into brick body
 * - Position: Mirrors stud positions on bottom surface
 * - Material: Darker shade of brick color
 *
 * Note: For placed bricks, sockets can be hidden since they're on the bottom.
 * For preview bricks, showing them helps visualize the connection system.
 *
 * @param type - The brick type (determines socket count/layout)
 * @param rotation - Rotation in degrees
 * @param color - RGB color for the sockets (usually darker than brick)
 * @param visible - Whether to render sockets (default: false for placed, true for preview)
 */
export const BrickSockets: React.FC<BrickSocketsProps> = ({
  type,
  rotation,
  color,
  visible = false,
}) => {
  // Calculate socket positions relative to brick center
  const socketPositions = useMemo(
    () => generateSocketPositions(type, rotation),
    [type, rotation]
  )

  const { STUD_DIAMETER } = BRICK_CONSTANTS
  const socketInnerRadius = (STUD_DIAMETER / 2) * 1.05 // Slightly larger than stud
  const socketOuterRadius = socketInnerRadius + 0.05 // Wall thickness
  const socketDepth = 0.3 // How deep the socket extends into brick

  // Darker shade of brick color for sockets
  const socketColor = `rgb(${Math.floor(color.r * 0.7)}, ${Math.floor(color.g * 0.7)}, ${Math.floor(color.b * 0.7)})`

  // Don't render if not visible
  if (!visible) return null

  return (
    <group>
      {socketPositions.map((position, index) => (
        <mesh
          key={`socket-${index}`}
          position={[position.x, position.y, position.z]}
          receiveShadow
        >
          {/* Tube geometry: innerRadius, outerRadius, height, radialSegments */}
          <cylinderGeometry
            args={[socketOuterRadius, socketOuterRadius, socketDepth, 16]}
          />
          <meshStandardMaterial
            color={socketColor}
            roughness={0.4}
            metalness={0.0}
            side={2} // DoubleSide to see inside
          />
        </mesh>
      ))}
    </group>
  )
}
